import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as api from './tcs-challans.api';

import type {
  CreateTcsChallanRequest,
  TcsDocumentKind,
  UpdateTcsChallanRequest,
} from '@utazzo/shared';

const REGISTER_KEY = ['reports', 'tcs-register'] as const;
const LIST_KEY = ['tcs-challans', 'list'] as const;
const detailKey = (id: string | null) => ['tcs-challans', 'detail', id] as const;

const messageOf = (err: unknown, fallback: string): string => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  return fallback;
};

/** Every mutation refreshes the register, the challan list and (if known) the detail. */
const useRefresh = () => {
  const qc = useQueryClient();
  return (challanId?: string | null) => {
    void qc.invalidateQueries({ queryKey: REGISTER_KEY });
    void qc.invalidateQueries({ queryKey: LIST_KEY });
    if (challanId) void qc.invalidateQueries({ queryKey: detailKey(challanId) });
  };
};

export const useChallanList = (params?: { paid?: boolean; q?: string }, enabled = true) =>
  useQuery({
    queryKey: [...LIST_KEY, params ?? {}],
    queryFn: () => api.listChallans(params),
    enabled,
  });

export const useChallanDetail = (id: string | null) =>
  useQuery({
    queryKey: detailKey(id),
    queryFn: () => api.getChallan(id as string),
    enabled: Boolean(id),
  });

export const useCreateChallan = () => {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (body: CreateTcsChallanRequest) => api.createChallan(body),
    onSuccess: (c) => refresh(c.id),
    onError: (err: unknown) => toast.error(messageOf(err, 'Failed to create the challan')),
  });
};

/** Mark paid/unpaid or edit fields. */
export const usePatchChallan = () => {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateTcsChallanRequest }) =>
      api.patchChallan(id, body),
    onSuccess: (_r, v) => refresh(v.id),
    onError: (err: unknown) => toast.error(messageOf(err, 'Failed to update the challan')),
  });
};

export const useAddInvoices = () => {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ id, invoiceIds }: { id: string; invoiceIds: string[] }) =>
      api.addInvoices(id, invoiceIds),
    onSuccess: (_r, v) => refresh(v.id),
    onError: (err: unknown) => toast.error(messageOf(err, 'Failed to add invoices')),
  });
};

export const useRemoveInvoice = () => {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ id, invoiceId }: { id: string; invoiceId: string }) =>
      api.removeInvoice(id, invoiceId),
    onSuccess: (_r, v) => refresh(v.id),
    onError: (err: unknown) => toast.error(messageOf(err, 'Failed to remove the invoice')),
  });
};

export const useDeleteChallan = () => {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (id: string) => api.deleteChallan(id),
    onSuccess: (_r, id) => refresh(id),
    onError: (err: unknown) => toast.error(messageOf(err, 'Failed to delete the challan')),
  });
};

/** Upload one file: presign -> PUT -> create row. */
export const useUploadDoc = () => {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ id, file, kind }: { id: string; file: File; kind: TcsDocumentKind }) =>
      api.uploadDoc(id, file, kind),
    onSuccess: (_d, v) => refresh(v.id),
  });
};

export const useDeleteDoc = () => {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ id, docId }: { id: string; docId: string }) => api.deleteDoc(id, docId),
    onSuccess: (_r, v) => refresh(v.id),
    onError: (err: unknown) => toast.error(messageOf(err, 'Failed to delete the document')),
  });
};
